// @ts-nocheck
import { ISceneLayerGeometryOpts } from "../interfaces";
import { getGeometryHash } from "../provider/wasm/utils";
import SceneLayer from "./layer";

export default class SceneLayerGeometry extends SceneLayer {
	_options: ISceneLayerGeometryOpts;

	_lastHash: string;

	/**
	 * Vytvoreni vrstvy pro geometrie.
	 *
	 * @param  {String|Number} [id] Volitelne ID
	 */
	constructor(scene, itemId, id, options: ISceneLayerGeometryOpts) {
		super(scene, itemId, id);

		// puvodne se tam nic neposilalo
		this._geometries = {};
		this._smartGeometries = {};
		this._lastHash = "";
		this._options = {
			useCache: false,
			topLevel: false,
			...options,
		};
	}

	/**
	 * Destruktor.
	 */
	destroy() {
		this.clear();

		this._geometries = {};
		this._smartGeometries = {};

		super.destroy();
	}

	/**
	 * Informace ohledne entity ve scene.
	 *
	 * @return {Object}
	 */
	// eslint-disable-next-line class-methods-use-this
	get featureInfo() {
		return {
			name: "SceneLayerGeometry",
			type: "layer",
		};
	}

	/**
	 * Souhrn vsech dat, ktere vyuziva visualiser pri vytvoreni.
	 *
	 * @return {Object}
	 */
	getCreateData() {
		const rd = super.getCreateData();

		rd.options = this._options;
		rd.geometries = this._geometries;

		return rd;
	}

	/**
	 * Nastaveni vrstvy.
	 */
	setOptions(...args) {
		const options = args[0];

		for (const param in options) {
			if (param in options) {
				this._options[param] = options[param];
			}
		}

		this._onChange(this._scene.featureEvent("layerGeometrySetOptions", true, true), args);
	}

	/**
	 * Pridani geometrie.
	 *
	 * @param {SceneGeometry} geometry
	 */
	addGeometry(...args) {
		const hash = this._options.useCache && this.appended ? getGeometryHash(geometry) : "";

		this._addGeometry(args[0]);

		this._onChange(this._scene.featureEvent("layerGeometryAddGeometry", true, true), args);

		this._lastHash = hash;

		return args[0];
	}

	addSmartGeometry(...args) {
		const hash = this._options.useCache && this.appended ? getGeometryHash(geometry) : "";
		const geometries = args[0].geometries;

		args[0].appended = true;
		args[0].sceneOwner = this;

		for (let index = 0; index < geometries.length; index++) {
			this._addGeometry(geometries[index]);

			this._onChange(this._scene.featureEvent("layerGeometryAddGeometry", true, true), [geometries[index]]);
		}

		this._smartGeometries[args[0].getId()] = args[0];
		this._lastHash = hash;

		return args[0];
	}

	/**
	 * Odebrani geometrie.
	 *
	 * @param {SceneGeometry} geometry
	 */
	removeGeometry(...args) {
		args[0].clear();

		args[0].appended = false;
		args[0].sceneOwner = null;

		delete this._geometries[args[0].getId()];

		this._onChange(this._scene.featureEvent("layerGeometryRemoveGeometry", true, true), args);
		this._lastHash = "";
	}

	/**
	 * Odebrani smart geometrie.
	 *
	 * @param {SceneGeometry} geometry
	 */
	removeSmartGeometry(geometry) {
		const geometries = geometry.geometries;

		for (let index = 0; index < geometries.length; index++) {
			if (geometries[index].appended) {
				this.removeGeometry(geometries[index]);
			}
		}

		geometry.clear();

		geometry.appended = false;
		geometry.sceneOwner = null;

		delete this._smartGeometries[geometry.getId()];

		this._lastHash = "";
	}

	/**
	 * Odebrani vsech geometrii.
	 */
	removeAll() {
		const list = [];

		for (const id in this._geometries) {
			if (id in this._geometries) {
				list.push(this._geometries[id]);
			}
		}

		while (list.length) {
			this.removeGeometry(list.shift());
		}

		for (const id in this._smartGeometries) {
			if (id in this._smartGeometries) {
				list.push(this._smartGeometries[id]);
			}
		}

		while (list.length) {
			this.removeSmartGeometry(list.shift());
		}

		this._lastHash = "";

		super.removeAll();
	}

	/**
	 * prekresleni smart geometrie
	 */
	redrawSmartGeometry(geometry) {
		const hash = this._options.useCache ? getGeometryHash(geometry) : "";
		let geometries = geometry.geometries;

		if (this._options.useCache && hash === this._lastHash) {
			/* eslint-disable-next-line */
			console.log(["cache hit"]);

			return;
		}

		for (let index = 0; index < geometries.length; index++) {
			const appended = geometries[index].appended;

			geometries[index].clear();
			geometries[index].appended = false;
			geometries[index].sceneOwner = null;

			delete this._geometries[geometries[index].getId()];

			if (!appended) {
				continue;
			}
			this._onChange(this._scene.featureEvent("layerGeometryRemoveGeometry", true, true), [geometries[index]]);
		}

		geometries = geometry.redraw();

		for (let index = 0; index < geometries.length; index++) {
			this._addGeometry(geometries[index]);

			this._onChange(this._scene.featureEvent("layerGeometryAddGeometry", true, true), [geometries[index]]);
		}

		this._lastHash = hash;
	}

	/**
	 * Vycisteni vsech geometrii.
	 */
	clear() {
		for (const id in this._geometries) {
			if (id in this._geometries) {
				this._geometries[id].clear();
			}
		}

		super.clear();
	}

	/**
	 * Pridani geometrie.
	 *
	 * @param {SceneGeometry} geometry
	 * @private
	 */
	_addGeometry(geometry) {
		const id = geometry.getId(); /* pokud uz byla, tak tu starou aspon smazeme */

		if (this._geometries[id]) {
			this.removeGeometry(this._geometries[id]);
		}

		this._geometries[geometry.getId()] = geometry;
		geometry.appended = true;
		geometry.sceneOwner = this;
	}

	/**
	 * Ziskani objektu se vsemi geometriemi.
	 *
	 * @return {Array}
	 */
	getGeometries() {
		return this._geometries;
	}

	/**
	 * Ziskani pole vsech geometrii.
	 *
	 * @return {Array}
	 */
	getGeometriesList() {
		const items = [];

		for (const id in this._geometries) {
			if (id in this._geometries) {
				items.push(this._geometries[id]);
			}
		}

		return items;
	}
}
